import type { FanResponseDto } from '@api'
import emojiRegexGenerator from 'emoji-regex'

import type { ScriptNode } from '@/modules/scripts/types.ts'

const emojiRegex = emojiRegexGenerator()
const onlyFansDefaultNameRegex = new RegExp('^u(\\d)+$')
const displayNameVariables = ['%name', '%lname', '%uname%']
const fallbackVariableResolver = (
  fan: FanResponseDto,
  fallbackVariable: string | null,
) =>
  fallbackVariable != null &&
  variableResolvers[fallbackVariable] &&
  !displayNameVariables.includes(fallbackVariable)
    ? variableResolvers[fallbackVariable](fan, fallbackVariable)
    : null
const displayNameResolver = (
  fan: FanResponseDto,
  transformer: (_arg: string) => string,
  fallbackVariable: string | null,
) =>
  fan.displayName != null && fan.displayName.length > 0
    ? transformer(processFanName(fan.displayName, true))
    : fallbackVariableResolver(fan, fallbackVariable)
const nickNameResolver = (
  fan: FanResponseDto,
  onlyFirst: boolean,
  transformer: (_arg: string) => string,
) =>
  fan.name != null &&
  fan.name.length > 0 &&
  !onlyFansDefaultNameRegex.test(fan.name)
    ? transformer(processFanName(fan.name, onlyFirst))
    : null

const variableResolvers: Record<
  string,
  (fan: FanResponseDto, fallbackVariable: string | null) => string | null
> = {
  '%name%': (fan, fallbackVariable) =>
    displayNameResolver(fan, (name) => name, fallbackVariable),
  '%lname%': (fan, fallbackVariable) =>
    displayNameResolver(
      fan,
      (name: string) => name.toLowerCase(),
      fallbackVariable,
    ),
  '%uname%': (fan, fallbackVariable) =>
    displayNameResolver(
      fan,
      (name: string) => name.toUpperCase(),
      fallbackVariable,
    ),
  '%nick%': (fan, _fallbackVariable) =>
    nickNameResolver(fan, false, (name: string) => name),
  '%lnick%': (fan, _fallbackVariable) =>
    nickNameResolver(fan, false, (name: string) => name.toLowerCase()),
  '%unick%': (fan, _fallbackVariable) =>
    nickNameResolver(fan, false, (name: string) => name.toUpperCase()),
  '%fnick%': (fan, _fallbackVariable) =>
    nickNameResolver(fan, true, (name: string) => name),
  '%fnickl%': (fan, _fallbackVariable) =>
    nickNameResolver(fan, true, (name: string) => name.toLowerCase()),
  '%fnicku%': (fan, _fallbackVariable) =>
    nickNameResolver(fan, true, (name: string) => name.toUpperCase()),
}

export function getFormattedMessageByMessageObject(
  message: ScriptNode['message'],
  fan: FanResponseDto,
) {
  let finalText = message?.text

  const fallbackVariable = message?.enableFallbackVariable
    ? message.fallbackVariable
    : null

  for (const [key, value] of Object.entries(variableResolvers)) {
    if (finalText?.includes(key)) {
      const regExp = new RegExp(key, 'g')
      const replacement =
        value(fan, fallbackVariable ?? '') ?? message?.fallback

      finalText = finalText.replace(regExp, replacement ?? '')
    }
  }

  return finalText
}

function processFanName(name: string, onlyFirst = true) {
  let formattedName = name
    // Removing emojis
    .replace(emojiRegex, '')
    // Removing numbers and spaces
    .replace(/[0-9]+/g, '')
    .trim()

  if (onlyFirst && formattedName.includes(' ')) {
    formattedName = formattedName.substring(0, formattedName.indexOf(' '))
  }

  return formattedName
}
